/**
 * KioskSlideshow Component
 *
 * Uses React Query for fetching and caching, refetches at 3, 15, 24 minutes past the hour.
 * Skips querying for non-operational stations.
 *
 * @component
 */

/**
 * StationSlide
 *
 * Shows live weather for one station as a slide.
 * Skips non-operational stations. Can also show a chart.
 *
 * summary:
 * - Fetches current and last-week weather via react-query
 * - Only queries for operational stations to reduce load
 * - Honors slide language preference (English/Inuktitut/Syllabics)
 */
import React, { useEffect } from "react";
import { Box, Typography, Paper, Skeleton, Alert } from "@mui/material";
import WeatherDashboard from "../../../WeatherDashboard/WeatherDashboard";
import WeatherChart from "../../../Chart/WeatherChart";
import TidePrediction from "../../../TidePrediction/TidePrediction";
import axios from "axios";
import { useQuery } from "react-query";
import { stationMetaData } from "../../../../utils/rawData";
import {
  fetchCurrentWeather,
  fetchLastWeekWeather,
  fetchTidePrediction,
  fetchLunarSolarData,
} from "../../utils/kioskApi";
import { refetchIntervalFn } from "../../utils/kioskRefetch";
import {
  renderNoStations,
  renderNonOperational,
  renderLoading,
  renderError,
} from "../SlideShowUtilsComponents";
import LunarSolar from "../../../LunarSolar/LunarSolar";
import { useTranslation } from "../../../../context/TranslationContext";
import { languageService } from "../../../../context/languageService";

// --- React Query Refetch Helper ---
/**
 * Only refetch at 3, 15, 24 minutes past the hour
 */

// --- UI Renderers ---

// --- Main Component ---
export default function StationSlide({
  config,
  stationIndex,
  language,
  isFullscreen,
  fullscreenContainerRef,
}) {
  const { setCurrentLanguage } = useTranslation();

  // Set the current language for this slide
  useEffect(() => {
    if (language) {
      // If the slide language is inuktitut, but user prefers syllabics, use syllabics
      // If the slide language is inuktitut and user prefers inuktitut, use inuktitut
      // If the slide language is english, use english
      const currentUserLanguage = languageService.getPreferredLanguage();
      const languageToUse =
        language === "inuktitut" && currentUserLanguage === "syllabics"
          ? "syllabics"
          : language;
      console.log(
        `StationSlide: Setting language from ${language} to ${languageToUse} (user pref: ${currentUserLanguage})`
      );
      setCurrentLanguage(languageToUse);
    }
  }, [language, setCurrentLanguage]);

  // Extract and sort stations by order
  const stations = (config?.config?.stations || [])
    .slice()
    .sort((a, b) => a.order - b.order);
  const currentStation = stations[stationIndex];
  const stationId = currentStation?.id;
  const stationMeta = stationMetaData.find((s) => s.id === stationId);
  const isOperational = stationMeta?.status === "Operational";
  const isWIRLStation = stationMeta?.type === "WIRL_Weather_Station";

  // React Query for current weather (only if operational)
  const {
    data: currentData,
    isLoading: loading,
    isError,
    error,
  } = useQuery(
    ["currentWeather", stationId],
    () => fetchCurrentWeather(stationId),
    {
      enabled: !!stationId && isOperational,
      staleTime: 60 * 60 * 1000, // 1 hour
      refetchInterval: refetchIntervalFn,
      refetchIntervalInBackground: true,
      retry: 1,
    }
  );

  // React Query for last week weather (only if operational)
  const {
    data: lastWeekData,
    isLoading: weekDataLoading,
    isError: weekIsError,
    error: weekDataError,
  } = useQuery(
    ["lastWeekWeather", stationId],
    () => fetchLastWeekWeather(stationId),
    {
      enabled: !!stationId && isOperational,
      staleTime: 60 * 60 * 1000, // 1 hour
      refetchInterval: refetchIntervalFn,
      refetchIntervalInBackground: true,
      retry: 1,
    }
  );

  // React Query for Lunar-Solar data (only if operational)
  const {
    data: lunarSolarData,
    isLoading: lunarSolarLoading,
    isError: lunarSolarIsError,
    error: lunarSolarError,
  } = useQuery(
    ["lunarSolarData", stationId],
    () => fetchLunarSolarData(stationId),
    {
      enabled: !!stationId && isOperational,
      staleTime: 60 * 60 * 1000, // 1 hour
      refetchInterval: refetchIntervalFn,
      refetchIntervalInBackground: true,
      retry: 1,
    }
  );

  // React Query for tide prediction (only if operational and WIRL station)
  // const {
  //   data: tideData,
  //   isLoading: tideLoading,
  //   isError: tideIsError,
  //   error: tideError,
  // } = useQuery(
  //   ["tidePrediction", stationId],
  //   () => fetchTidePrediction(stationId),
  //   {
  //     enabled: !!stationId && isOperational,
  //     staleTime: 60 * 60 * 1000, // 1 hour
  //     refetchInterval: refetchIntervalFn,
  //     refetchIntervalInBackground: true,
  //     retry: 1,
  //   }
  // );

  if (!stations.length) return renderNoStations();
  if (!isOperational)
    return renderNonOperational(currentStation?.name || stationId);
  if (loading) return renderLoading();
  if (isError) return renderError(currentStation?.name, stationId, error);

  // --- Main Weather Dashboard UI ---
  return (
    <>
      {/* Weather dashboard for the current station */}
      {currentData && currentStation?.id && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
            gap: 4,
            alignItems: "stretch",
            justifyContent: "center",
          }}
        >
          {/* WeatherDashboard - left side (50%) */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <WeatherDashboard
              data={currentData}
              stationId={currentStation.id}
              // tideData={tideData}
              // tideLoading={tideLoading}
              // tideError={tideError}
            />
          </Box>

          {/* WeatherChart - right side (50%) */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Lunar Solar Section - first row */}
            {/* {lunarSolarData && (
              <Box>
                <LunarSolar
                  lunarSolarData={lunarSolarData}
                  loading={lunarSolarLoading}
                  error={lunarSolarError}
                  padding={2}
                />
              </Box>
            )} */}

            {/* Weather Chart - second row */}
            {weekDataLoading ? null : weekIsError ? (
              <Alert severity="error" sx={{ width: "100%" }}>
                {weekDataError?.message || weekDataError}
              </Alert>
            ) : lastWeekData ? (
              <WeatherChart
                weekWeatherData={lastWeekData}
                isFullscreen={isFullscreen}
                fullscreenContainerRef={fullscreenContainerRef}
              />
            ) : null}
          </Box>
        </Box>
      )}
    </>
  );
}
